import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
	casesLawyerService,
	GET_CLAIM_FORM_QUESTIONS,
	GET_LAWYER_CASE_DETAILS,
	lawyerDirectoryService,
} from '@/api';
import { CaseApplicationQuestion, FollowUpList } from '@/interfaces';
import { SelectedAnswer } from '@/components/screens/lawyerapp/cases/case-clients/filters/document-filter-follow-up/DocumentFilterPanel';
import { useFilters } from '../useFilters';
import { getValidCaseId } from '@/utils';

const CLAIM_FORM_FOLLOW_UP_ID = -1;

const toClaimFormFollowUpList = (
	questions: CaseApplicationQuestion[] = [],
	brandNameById: Record<string, string> = {}
): FollowUpList[] => {
	if (questions.length === 0) return [];

	return [
		{
			id: CLAIM_FORM_FOLLOW_UP_ID,
			name: 'Claim Form',
			questions: questions.map((question) => {
				let options:
					| {
							id?: number;
							text: string;
							detail?: string;
							is_correct: boolean;
					  }[]
					| null = null;

				if (question.question_type === 'MULTIPLE') {
					options = question.multiple_choise_questions.map((choice) => ({
						id: choice.id,
						text: brandNameById[choice.brand_id] ?? 'None',
						detail: choice.complement,
						is_correct: choice.is_correct,
					}));
				} else if (question.question_type === 'YES_NO') {
					const isBoth = question.expected_answer === 'BOTH';
					options = [
						{
							text: 'Yes',
							is_correct: isBoth || question.expected_answer === 'YES',
						},
						{
							text: 'No',
							is_correct: isBoth || question.expected_answer === 'NO',
						},
					];
				} else if (question.question_type === 'DATE') {
					const [from, to] = (question.expected_answer ?? '').split('-');
					options = [
						{ text: from ?? '', is_correct: false },
						{ text: to ?? '', is_correct: false },
					];
				}

				return {
					id: question.id,
					follow_up_id: CLAIM_FORM_FOLLOW_UP_ID,
					text: question.question,
					type: question.question_type,
					automatic_approval: false,
					explanation: false,
					numerical_order: question.numerical_order,
					options,
					created_at: '',
					updated_at: '',
				};
			}),
		},
	];
};

export const useClaimFormFilter = (caseId: string | undefined) => {
	const { setParamFilter } = useFilters();
	const validCaseId = getValidCaseId(caseId);

	const { data: claimFormList, isLoading } = useQuery({
		queryKey: [GET_CLAIM_FORM_QUESTIONS, validCaseId],
		queryFn: () =>
			lawyerDirectoryService.getClaimFormQuestions(validCaseId as string),
		enabled: !!validCaseId,
	});

	const { data: caseDetails } = useQuery({
		queryKey: [GET_LAWYER_CASE_DETAILS, validCaseId],
		queryFn: () => casesLawyerService.getCaseDetails(validCaseId as string),
		enabled: !!validCaseId,
	});

	const brandNameById = React.useMemo(() => {
		const map: Record<string, string> = {};
		caseDetails?.data.brands?.forEach((brand) => {
			if (brand.id && brand.name) map[brand.id.toString()] = brand.name;
		});
		return map;
	}, [caseDetails]);

	const list = React.useMemo(
		() => toClaimFormFollowUpList(claimFormList?.data, brandNameById),
		[claimFormList, brandNameById]
	);

	const yesNoQuestionIds = React.useMemo(() => {
		const ids = new Set<number>();
		(claimFormList?.data ?? []).forEach((question) => {
			if (question.question_type === 'YES_NO') ids.add(question.id);
		});
		return ids;
	}, [claimFormList]);

	const hasItems = list.length > 0;

	const applyFilter = (selectedAnswers: SelectedAnswer[]) => {
		const grouped = selectedAnswers.reduce<Record<number, string[]>>(
			(acc, sel) => {
				if (!acc[sel.questionId]) acc[sel.questionId] = [];
				const answerId = yesNoQuestionIds.has(sel.questionId)
					? sel.answerId.toUpperCase()
					: sel.answerId;
				acc[sel.questionId].push(answerId);
				return acc;
			},
			{}
		);

		const questions = Object.entries(grouped).map(([questionId, answers]) => ({
			questionId: Number(questionId),
			answers,
		}));

		setParamFilter(
			'claimFormQuestions',
			questions.length > 0 ? JSON.stringify(questions) : undefined
		);
	};

	return { list, isLoading, hasItems, applyFilter };
};
